import React, { useEffect, useState, useMemo, useCallback } from "react";
import axiosInstance from "../../../../services/axiosInstance";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  ThemeProvider,
  createTheme,
  CssBaseline,
  TablePagination,
  Stack,
  TextField,
  InputAdornment,
  Skeleton,
  Grid,
  Button,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Drawer,
  Divider,
  Link,
  Fade,
  Menu,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import {
  Search,
  Download,
  Calendar as CalendarIcon,
  User,
  Building,
  Mail,
  Phone,
  Filter,
  RefreshCw,
  Layers,
  X,
  MousePointerClick,
  FileText,
  Printer,
  FileSpreadsheet,
  ChevronDown,
  TrendingUp,
  Zap,
  CheckCircle2,
  Target,
  Award
} from "lucide-react";
import * as XLSX from "xlsx";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Sector,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LabelList
} from "recharts";
import SideNavbar from "../../../../components/sideNavbar";
import apiEndPointsConfig from "../../../../services/apiEndPoints";

const theme = createTheme({
  palette: {
    primary: { main: "#1E3A8A" },
    secondary: { main: "#2563EB" },
    background: { default: "#F8FAFC" },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Segoe UI', sans-serif",
    h5: { fontWeight: 800 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    body2: { fontSize: "0.875rem" },
  },
  shape: { borderRadius: 12 },
});

// Color palettes matching screenshots
const ASSIGNEE_COLORS = [
  "#2563EB", // Royal Blue
  "#7C3AED", // Violet
  "#059669", // Emerald Green
  "#D97706", // Amber
  "#0891B2", // Cyan
  "#EC4899", // Pink
  "#4F46E5", // Indigo
  "#E11D48"  // Rose
];

const STAGE_COLORS = [
  "#3B82F6", // Blue
  "#6366F1", // Indigo
  "#8B5CF6", // Violet
  "#10B981", // Emerald
  "#EF4444", // Rose
  "#F59E0B", // Amber
  "#06B6D4", // Cyan
  "#EC4899", // Pink
  "#84CC16"  // Lime
];

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Rebuilds the same "DD-Mon" bucket key the API uses for the daily charts,
// so clicking a day in a chart can drill the detail table to that date.
const toDayKey = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}-${MONTH_ABBR[d.getMonth()]}`;
};

// Mirrors the stage bucketing done by the report API so the KPI cards can
// filter the detail rows they are counting.
const stageCategory = (stageName = "") => {
  const s = String(stageName).toLowerCase();
  if (s.includes("converted") || s.includes("won")) return "converted";
  if (s.includes("disqualif") || s.includes("lost") || s.includes("junk") || s.includes("reject")) return "disqualified";
  if (s.includes("demo")) return "demos";
  if (s.includes("qualifi")) return "qualified";
  return "new";
};

const leadMatchesKpi = (lead, kpi) => {
  const cat = stageCategory(lead.stage);
  switch (kpi) {
    case "open":
      return cat !== "converted" && cat !== "disqualified";
    case "qualified":
      return cat === "qualified";
    case "demos":
      return cat === "demos";
    case "converted":
      return cat === "converted";
    case "disqualified":
      return cat === "disqualified";
    case "pipeline":
      return cat === "qualified" || cat === "demos" || cat === "converted";
    default:
      return true;
  }
};

const TopMetricCard = ({ title, value, headerBg, textColor, loading, onClick, active, dimmed }) => {
  const clickable = Boolean(onClick);
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (clickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: active ? `2px solid ${headerBg || "#1E3A8A"}` : "1px solid #CBD5E1",
        bgcolor: "#EBF3FE",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: clickable ? "pointer" : "default",
        opacity: dimmed ? 0.5 : 1,
        transform: active ? "translateY(-3px)" : "none",
        transition: "transform .18s ease, box-shadow .18s ease, opacity .18s ease, border-color .18s ease",
        boxShadow: active
          ? `0 10px 22px -6px ${headerBg || "#1E3A8A"}66`
          : "0 2px 8px rgba(0,0,0,0.04)",
        outline: "none",
        "&:hover": clickable
          ? { transform: "translateY(-3px)", boxShadow: "0 10px 22px -8px rgba(15,23,42,0.28)", opacity: 1 }
          : undefined,
        "&:focus-visible": { boxShadow: `0 0 0 3px ${headerBg || "#1E3A8A"}44` }
      }}
    >
      {/* Banner Header */}
      <Box
        sx={{
          bgcolor: headerBg || "#1E3A8A",
          py: 0.75,
          px: 1,
          textAlign: "center"
        }}
      >
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: "0.7rem",
            fontWeight: 800,
            letterSpacing: 0.6,
            textTransform: "uppercase"
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Body Metric */}
      <Box
        sx={{
          py: 1.8,
          px: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexGrow: 1
        }}
      >
        {loading ? (
          <Skeleton width="60%" height={32} />
        ) : (
          <Typography
            sx={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: textColor || "#1E3A8A",
              lineHeight: 1
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const ChartCard = ({ title, hint, children, action }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 3,
      border: "1px solid #E2E8F0",
      bgcolor: "#FFFFFF",
      boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      transition: "box-shadow .2s ease",
      "&:hover": { boxShadow: "0 8px 28px -6px rgba(15,23,42,0.12)" }
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" sx={{ fontSize: "1.05rem", color: "#0F172A", fontWeight: 800 }}>
          {title}
        </Typography>
        {hint && (
          <Tooltip title={hint} arrow>
            <Box sx={{ display: "flex", color: "#94A3B8" }}>
              <MousePointerClick size={15} />
            </Box>
          </Tooltip>
        )}
      </Stack>
      {action}
    </Stack>
    <Box sx={{ flexGrow: 1, width: "100%", minHeight: 280 }}>
      {children}
    </Box>
  </Paper>
);

// Builds the per-sector renderer for a pie: the hovered or drilled-into slice
// pops out and prints a readout in the donut hole, everything else dims.
// Recharts v3 drives this through `shape`, which receives `isActive` and `index`.
const makePieSectorRenderer = ({ hoveredIndex, selectedName, colorAt }) => (props) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle, cornerRadius, index, name, value, isActive
  } = props;

  const isHovered = isActive || index === hoveredIndex;
  const isSelected = Boolean(selectedName) && name === selectedName;
  const expanded = isHovered || isSelected;
  const showReadout = isHovered || (hoveredIndex === -1 && isSelected);
  const percentage = props.percentage ?? props.payload?.percentage ?? "";
  const fill = props.fill || colorAt(index);
  const dimmed = Boolean(selectedName) && !isSelected;

  return (
    <g>
      {showReadout && (
        <>
          <text x={cx} y={cy - 6} textAnchor="middle" fill="#0F172A" style={{ fontSize: 12, fontWeight: 800 }}>
            {name}
          </text>
          <text x={cx} y={cy + 13} textAnchor="middle" fill="#64748B" style={{ fontSize: 12, fontWeight: 700 }}>
            {value}{percentage ? ` (${percentage})` : ""}
          </text>
        </>
      )}
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={expanded ? outerRadius + 7 : outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        cornerRadius={cornerRadius}
        fill={fill}
        fillOpacity={dimmed ? 0.28 : 1}
        stroke={isSelected ? "#0F172A" : "none"}
        strokeWidth={isSelected ? 2 : 0}
      />
      {expanded && (
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={outerRadius + 9}
          outerRadius={outerRadius + 12}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          fillOpacity={dimmed ? 0.28 : 1}
        />
      )}
    </g>
  );
};

const EmptyChart = ({ message }) => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
    <Typography color="textSecondary">{message}</Typography>
  </Box>
);

const DetailRow = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
    <Box sx={{ color: "#94A3B8", mt: "2px" }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: "#94A3B8", fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", fontSize: "0.65rem" }}
      >
        {label}
      </Typography>
      <Box sx={{ color: "#1E293B", fontSize: "0.88rem", fontWeight: 600, wordBreak: "break-word" }}>
        {value || "—"}
      </Box>
    </Box>
  </Stack>
);

const TABLE_COLUMNS = [
  { id: "name", label: "Lead Name" },
  { id: "companyName", label: "Company" },
  { id: "phone", label: "Contact" },
  { id: "stage", label: "Stage" },
  { id: "accountManager", label: "Assignee" },
  { id: "source", label: "Source" },
  { id: "createdAt", label: "Created Date" }
];

const UnreachedLeadsReport = () => {
  const [data, setData] = useState({
    summary: {
      totalLeads: 0,
      avgLeadsPerDay: 0,
      open: 0,
      qualified: 0,
      demos: 0,
      converted: 0,
      disqualified: 0,
      pipelineWonPct: "0.0%"
    },
    stageBreakdown: [],
    dailyTrend: [],
    assigneeBreakdown: [],
    dailyAssigneeStacked: [],
    cumulativeTrend: [],
    assigneesList: [],
    stagesList: [],
    teamMembersList: [],
    details: []
  });

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState("all");
  const [selectedStage, setSelectedStage] = useState("all");
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: ""
  });
  const [activePreset, setActivePreset] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // --- Interactive state --------------------------------------------------
  // Client-side drill-down driven by clicking cards, chart slices and bars.
  const [drill, setDrill] = useState({ kpi: null, stage: null, assignee: null, date: null });
  const [activeStageSlice, setActiveStageSlice] = useState(-1);
  const [activeAssigneeSlice, setActiveAssigneeSlice] = useState(-1);
  const [hiddenSeries, setHiddenSeries] = useState([]);
  const [sort, setSort] = useState({ key: "createdAt", dir: "desc" });
  const [selectedLead, setSelectedLead] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const isExportOpen = Boolean(exportAnchorEl);

  const handleOpenExport = (e) => setExportAnchorEl(e.currentTarget);
  const handleCloseExport = () => setExportAnchorEl(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;
      if (selectedAssignee !== "all") params.assignee = selectedAssignee;
      if (selectedStage !== "all") params.stage = selectedStage;

      const res = await axiosInstance.get(`${apiEndPointsConfig.getUnreachedReport}/me`, { params });
      if (res.data) {
        setData({
          summary: res.data.summary || {},
          stageBreakdown: res.data.stageBreakdown || [],
          dailyTrend: res.data.dailyTrend || [],
          assigneeBreakdown: res.data.assigneeBreakdown || [],
          dailyAssigneeStacked: res.data.dailyAssigneeStacked || [],
          cumulativeTrend: res.data.cumulativeTrend || [],
          assigneesList: res.data.assigneesList || [],
          stagesList: res.data.stagesList || [],
          teamMembersList: res.data.teamMembersList || [],
          details: res.data.details || []
        });
      }
    } catch (err) {
      console.error("Error fetching report data:", err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedAssignee, selectedStage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleQuickPreset = (preset) => {
    const today = new Date();
    let start = new Date();
    let end = new Date();

    setActivePreset(preset);

    if (preset === "mtd") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
    } else if (preset === "7d") {
      start.setDate(today.getDate() - 7);
    } else if (preset === "30d") {
      start.setDate(today.getDate() - 30);
    } else if (preset === "all") {
      setDateRange({ startDate: "", endDate: "" });
      return;
    }

    setDateRange({
      startDate: start.toISOString().split("T")[0],
      endDate: end.toISOString().split("T")[0]
    });
  };

  // --- Drill-down handlers -------------------------------------------------
  const toggleDrill = useCallback((key, value) => {
    if (!value) return;
    setDrill((prev) => ({ ...prev, [key]: prev[key] === value ? null : value }));
  }, []);

  const clearDrill = useCallback((key) => {
    setDrill((prev) => ({ ...prev, [key]: null }));
  }, []);

  const clearAllDrills = useCallback(() => {
    setDrill({ kpi: null, stage: null, assignee: null, date: null });
  }, []);

  const toggleSeries = useCallback((name) => {
    if (!name) return;
    setHiddenSeries((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  }, []);

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc"
    }));
  };

  const activeDrillCount = Object.values(drill).filter(Boolean).length;

  // Reset paging whenever the working set changes underneath the table.
  useEffect(() => {
    setPage(0);
  }, [drill, searchQuery, data.details]);

  const filteredDetails = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (data.details || []).filter((lead) => {
      if (drill.stage && lead.stage !== drill.stage) return false;
      if (drill.assignee && lead.accountManager !== drill.assignee) return false;
      if (drill.date && toDayKey(lead.createdAt) !== drill.date) return false;
      if (drill.kpi && !leadMatchesKpi(lead, drill.kpi)) return false;
      if (!q) return true;
      return (
        lead.name?.toLowerCase().includes(q) ||
        lead.phone?.includes(q) ||
        lead.email?.toLowerCase().includes(q) ||
        lead.companyName?.toLowerCase().includes(q) ||
        lead.accountManager?.toLowerCase().includes(q) ||
        lead.stage?.toLowerCase().includes(q)
      );
    });
  }, [data.details, searchQuery, drill]);

  const sortedDetails = useMemo(() => {
    const rows = [...filteredDetails];
    const { key, dir } = sort;
    const factor = dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      if (key === "createdAt") {
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * factor;
      }
      return String(a[key] ?? "").localeCompare(String(b[key] ?? ""), undefined, { sensitivity: "base" }) * factor;
    });
    return rows;
  }, [filteredDetails, sort]);

  const paginatedData = useMemo(() => {
    return sortedDetails.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedDetails, page, rowsPerPage]);

  const handleExportExcel = () => {
    handleCloseExport();
    const wb = XLSX.utils.book_new();

    // 1. Executive Summary KPIs Sheet
    const summaryData = [
      { Metric: "TOTAL LEADS", Value: data.summary.totalLeads ?? 0 },
      { Metric: "AVG LEADS / DAY", Value: data.summary.avgLeadsPerDay ?? 0 },
      { Metric: "OPEN", Value: data.summary.open ?? 0 },
      { Metric: "QUALIFIED", Value: data.summary.qualified ?? 0 },
      { Metric: "DEMOS", Value: data.summary.demos ?? 0 },
      { Metric: "CONVERTED", Value: data.summary.converted ?? 0 },
      { Metric: "DISQUALIFIED", Value: data.summary.disqualified ?? 0 },
      { Metric: "PIPELINE + WON %", Value: data.summary.pipelineWonPct || "0.0%" }
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary KPIs");

    // 2. Stage Breakdown Sheet
    if (data.stageBreakdown && data.stageBreakdown.length > 0) {
      const stageRows = data.stageBreakdown.map(s => ({
        "Stage Name": s.name,
        "Lead Count": s.count,
        "Share (%)": s.percentage
      }));
      const wsStage = XLSX.utils.json_to_sheet(stageRows);
      XLSX.utils.book_append_sheet(wb, wsStage, "Stage Breakdown");
    }

    // 3. Assignee Breakdown Sheet
    if (data.assigneeBreakdown && data.assigneeBreakdown.length > 0) {
      const assigneeRows = data.assigneeBreakdown.map(a => ({
        "Assignee Name": a.name,
        "Lead Count": a.count,
        "Share (%)": a.percentage
      }));
      const wsAssignee = XLSX.utils.json_to_sheet(assigneeRows);
      XLSX.utils.book_append_sheet(wb, wsAssignee, "Assignee Breakdown");
    }

    // 4. Daily Trend Sheet
    if (data.dailyTrend && data.dailyTrend.length > 0) {
      const trendRows = data.dailyTrend.map(t => ({
        "Date": t.date,
        "Leads Received": t.count
      }));
      const wsTrend = XLSX.utils.json_to_sheet(trendRows);
      XLSX.utils.book_append_sheet(wb, wsTrend, "Daily Trend");
    }

    const exportRows = sortedDetails.map((lead) => ({
      "Lead Name": lead.name,
      "Company Name": lead.companyName,
      "Phone": lead.phone,
      "Email": lead.email,
      "Stage": lead.stage,
      "Assignee": lead.accountManager,
      "Source": lead.source,
      "Created Date": new Date(lead.createdAt).toLocaleString(),
      "Notes / Remarks": lead.notes || lead.remarks || ""
    }));
    const wsDetails = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(wb, wsDetails, "Lead Details");

    XLSX.writeFile(wb, `Lead_Pipeline_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const handleExportPdf = () => {
    handleCloseExport();
    setTimeout(() => {
      window.print();
    }, 150);
  };

  // Calculate dynamic XAxis tick interval to prevent date label overlap
  const getTickInterval = (dataLength) => {
    if (!dataLength || dataLength <= 10) return 0;
    if (dataLength <= 20) return 1;
    if (dataLength <= 35) return 2;
    if (dataLength <= 60) return 4;
    return Math.floor(dataLength / 10);
  };

  // Format header month year title
  const trendMonthYearTitle = useMemo(() => {
    if (data.dailyTrend && data.dailyTrend.length > 0) {
      const item = data.dailyTrend[0];
      const month = item.date.split("-")[1] || "";
      const year = item.isoKey ? item.isoKey.split("-")[0] : new Date().getFullYear();
      return `Leads Received per Day — ${month} ${year}`;
    }
    return "Leads Received per Day";
  }, [data.dailyTrend]);

  const kpiCards = [
    { key: "total", title: "TOTAL LEADS", value: data.summary.totalLeads ?? 0, headerBg: "#1B365D", textColor: "#1E3A8A" },
    { key: "static", title: "AVG LEADS / DAY", value: data.summary.avgLeadsPerDay ?? 0, headerBg: "#2563EB", textColor: "#2563EB" },
    { key: "open", title: "OPEN", value: data.summary.open ?? 0, headerBg: "#4B5563", textColor: "#4B5563" },
    { key: "qualified", title: "QUALIFIED", value: data.summary.qualified ?? 0, headerBg: "#1D4ED8", textColor: "#1D4ED8" },
    { key: "demos", title: "DEMOS", value: data.summary.demos ?? 0, headerBg: "#7C3AED", textColor: "#7C3AED" },
    { key: "converted", title: "CONVERTED", value: data.summary.converted ?? 0, headerBg: "#15803D", textColor: "#15803D" },
    { key: "disqualified", title: "DISQUALIFIED", value: data.summary.disqualified ?? 0, headerBg: "#B91C1C", textColor: "#B91C1C" },
    { key: "pipeline", title: "PIPELINE + WON %", value: data.summary.pipelineWonPct || "0.0%", headerBg: "#B45309", textColor: "#B45309" }
  ];

  // Dim chart marks that fall outside the current drill selection.
  const stageOpacity = (name) => (drill.stage && drill.stage !== name ? 0.28 : 1);
  const assigneeOpacity = (name) => (drill.assignee && drill.assignee !== name ? 0.28 : 1);
  const dateOpacity = (date) => (drill.date && drill.date !== date ? 0.3 : 1);

  const chartCursor = { cursor: "pointer" };
  const darkTooltip = {
    contentStyle: { backgroundColor: "#0F172A", borderRadius: 8, color: "#FFF", border: "none" },
    itemStyle: { color: "#FFF" },
    labelStyle: { color: "#94A3B8" }
  };

  return (
    <Box sx={{ ml: { xs: 0, md: "280px" }, minHeight: "100vh", bgcolor: "#F8FAFC", p: { xs: 2, md: 3 } }}>
      <style>{`
        @media print {
          body {
            background-color: #ffffff !important;
            color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print, nav, header, button, .MuiIconButton-root, .MuiTablePagination-root, .MuiSelect-root, .MuiInputBase-root {
            display: none !important;
          }
          .MuiBox-root {
            margin-left: 0 !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .MuiPaper-root {
            box-shadow: none !important;
            border: 1px solid #CBD5E1 !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            margin-bottom: 20px !important;
          }
          .recharts-responsive-container {
            width: 100% !important;
            height: 300px !important;
          }
          svg {
            max-width: 100% !important;
          }
        }
      `}</style>
      <SideNavbar />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="xl" disableGutters>

          {/* Top Bar Header & Controls */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 3,
              bgcolor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              boxShadow: "0 2px 12px rgba(0,0,0,0.03)"
            }}
          >
            {/* Title row: name on the left, refresh + export on the right */}
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1.5}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ color: "#0F172A", fontWeight: 700, fontSize: "1.3rem", lineHeight: 1.3 }}>
                  Leads Report
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748B", mt: 0.25 }}>
                  Lead volume, stages, assignee share and daily progress
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                <Tooltip title="Refresh report" arrow>
                  <span>
                    <IconButton
                      onClick={fetchData}
                      disabled={loading}
                      sx={{ width: 38, height: 38, border: "1px solid #E2E8F0", borderRadius: 2, color: "#475569", "&:hover": { bgcolor: "#F1F5F9" } }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          animation: loading ? "reportSpin 1s linear infinite" : "none",
                          "@keyframes reportSpin": {
                            from: { transform: "rotate(0deg)" },
                            to: { transform: "rotate(360deg)" }
                          }
                        }}
                      >
                        <RefreshCw size={16} />
                      </Box>
                    </IconButton>
                  </span>
                </Tooltip>

                <Button
                  variant="contained"
                  startIcon={<Download size={16} />}
                  endIcon={<ChevronDown size={14} />}
                  onClick={handleOpenExport}
                  sx={{ height: 38, px: 2, borderRadius: 2, bgcolor: "#1E3A8A", boxShadow: "none", "&:hover": { bgcolor: "#1D4ED8", boxShadow: "none" }, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                  className="no-print"
                >
                  Export
                </Button>
                <Menu
                  anchorEl={exportAnchorEl}
                  open={isExportOpen}
                  onClose={handleCloseExport}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{ sx: { mt: 0.5, borderRadius: 2, minWidth: 260, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" } }}
                  className="no-print"
                >
                  <MenuItem onClick={handleExportExcel} sx={{ py: 1.2, gap: 1.5 }}>
                    <FileSpreadsheet size={18} color="#15803D" />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>
                        Export Excel Workbook
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                        Includes Summary KPIs, Chart Data & Details
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleExportPdf} sx={{ py: 1.2, gap: 1.5 }}>
                    <Printer size={18} color="#2563EB" />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>
                        Export PDF / Print Dashboard
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                        Print visual charts & full dashboard
                      </Typography>
                    </Box>
                  </MenuItem>
                </Menu>
              </Stack>
            </Stack>

            {/* Toolbar: period presets + filters, one row on wide screens */}
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: "#F8FAFC",
                border: "1px solid #F1F5F9",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
                // every control the same height
                "& .MuiInputBase-root": { height: 38, bgcolor: "#FFFFFF", borderRadius: 2, fontSize: "0.875rem" },
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={["all", "mtd", "7d", "30d"].includes(activePreset) ? activePreset : null}
                onChange={(_, value) => value && handleQuickPreset(value)}
                sx={{
                  bgcolor: "#FFFFFF",
                  borderRadius: 2,
                  "& .MuiToggleButton-root": {
                    height: 38,
                    px: 1.75,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.825rem",
                    color: "#475569",
                    borderColor: "#E2E8F0",
                    whiteSpace: "nowrap",
                    "&.Mui-selected": { bgcolor: "#1E3A8A", color: "#FFFFFF", borderColor: "#1E3A8A", "&:hover": { bgcolor: "#1D4ED8" } },
                  },
                  "& .MuiToggleButton-root:first-of-type": { borderTopLeftRadius: 8, borderBottomLeftRadius: 8 },
                  "& .MuiToggleButton-root:last-of-type": { borderTopRightRadius: 8, borderBottomRightRadius: 8 },
                }}
              >
                <ToggleButton value="all">All time</ToggleButton>
                <ToggleButton value="mtd">Month to date</ToggleButton>
                <ToggleButton value="7d">Last 7 days</ToggleButton>
                <ToggleButton value="30d">Last 30 days</ToggleButton>
              </ToggleButtonGroup>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: "1 1 300px", minWidth: 0 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={dateRange.startDate}
                  onChange={(e) => {
                    setActivePreset("custom");
                    setDateRange({ ...dateRange, startDate: e.target.value });
                  }}
                />
                <Typography sx={{ color: "#94A3B8", fontSize: "0.85rem" }}>–</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={dateRange.endDate}
                  onChange={(e) => {
                    setActivePreset("custom");
                    setDateRange({ ...dateRange, endDate: e.target.value });
                  }}
                />
              </Stack>

              <FormControl size="small" sx={{ flex: "1 1 170px", minWidth: 150 }}>
                <InputLabel>Assignee</InputLabel>
                <Select
                  value={selectedAssignee}
                  label="Assignee"
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                >
                  <MenuItem value="all">All assignees</MenuItem>
                  {data.teamMembersList.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: "1 1 150px", minWidth: 140 }}>
                <InputLabel>Stage</InputLabel>
                <Select
                  value={selectedStage}
                  label="Stage"
                  onChange={(e) => setSelectedStage(e.target.value)}
                >
                  <MenuItem value="all">All stages</MenuItem>
                  {data.stagesList.map((stage) => (
                    <MenuItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Active drill-down chips */}
            <Fade in={activeDrillCount > 0} unmountOnExit>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 2, pt: 2, borderTop: "1px dashed #E2E8F0" }}
              >
                <Stack direction="row" spacing={0.7} alignItems="center" sx={{ color: "#64748B", mr: 0.5 }}>
                  <Filter size={15} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Drill-down:
                  </Typography>
                </Stack>

                {drill.kpi && (
                  <Chip
                    size="small"
                    label={`Metric: ${drill.kpi}`}
                    onDelete={() => clearDrill("kpi")}
                    deleteIcon={<X size={14} />}
                    sx={{ fontWeight: 700, bgcolor: "#EFF6FF", color: "#1D4ED8", textTransform: "capitalize" }}
                  />
                )}
                {drill.stage && (
                  <Chip
                    size="small"
                    label={`Stage: ${drill.stage}`}
                    onDelete={() => clearDrill("stage")}
                    deleteIcon={<X size={14} />}
                    sx={{ fontWeight: 700, bgcolor: "#F5F3FF", color: "#6D28D9" }}
                  />
                )}
                {drill.assignee && (
                  <Chip
                    size="small"
                    label={`Assignee: ${drill.assignee}`}
                    onDelete={() => clearDrill("assignee")}
                    deleteIcon={<X size={14} />}
                    sx={{ fontWeight: 700, bgcolor: "#ECFDF5", color: "#047857" }}
                  />
                )}
                {drill.date && (
                  <Chip
                    size="small"
                    label={`Day: ${drill.date}`}
                    onDelete={() => clearDrill("date")}
                    deleteIcon={<X size={14} />}
                    sx={{ fontWeight: 700, bgcolor: "#FFF7ED", color: "#C2410C" }}
                  />
                )}

                <Typography variant="body2" sx={{ color: "#64748B" }}>
                  {filteredDetails.length} of {data.details.length} leads
                </Typography>

                <Button size="small" onClick={clearAllDrills} sx={{ color: "#DC2626", fontWeight: 700 }}>
                  Clear all
                </Button>
              </Stack>
            </Fade>
          </Paper>

          {/* 1. TOP METRIC CARDS BANNER (8 CARDS ROW - MATCHING IMAGE 1) */}
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {kpiCards.map((card) => {
              const isStatic = card.key === "static";
              const isTotal = card.key === "total";
              const isFilter = !isStatic && !isTotal;
              return (
                <Grid item xs={6} sm={4} md={3} lg={1.5} key={card.title}>
                  <Tooltip
                    title={isStatic ? "" : isTotal ? "Clear all drill-downs" : `Filter the table to ${card.title.toLowerCase()} leads`}
                    arrow
                  >
                    <Box sx={{ height: "100%" }}>
                      <TopMetricCard
                        title={card.title}
                        value={card.value}
                        headerBg={card.headerBg}
                        textColor={card.textColor}
                        loading={loading}
                        active={isFilter && drill.kpi === card.key}
                        dimmed={isFilter && Boolean(drill.kpi) && drill.kpi !== card.key}
                        onClick={
                          isStatic
                            ? undefined
                            : isTotal
                              ? clearAllDrills
                              : () => toggleDrill("kpi", card.key)
                        }
                      />
                    </Box>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>



          {/* 2. STAGE BREAKDOWN SECTION (2 SIDE-BY-SIDE CHARTS - MATCHING IMAGE 2) */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Left: Leads by Stage (Pie Chart) */}
            <Grid item xs={12} md={6}>
              <ChartCard title="Leads by Stage" hint="Click a slice or legend entry to filter the table by stage">
                {loading ? (
                  <Skeleton variant="rectangular" height="100%" />
                ) : data.stageBreakdown.length === 0 ? (
                  <EmptyChart message="No stage data available" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart style={chartCursor}>
                      <Pie
                        data={data.stageBreakdown}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={95}
                        innerRadius={50}
                        paddingAngle={3}
                        isAnimationActive={true}
                        shape={makePieSectorRenderer({
                          hoveredIndex: activeStageSlice,
                          selectedName: drill.stage,
                          colorAt: (i) => data.stageBreakdown[i]?.color || STAGE_COLORS[i % STAGE_COLORS.length]
                        })}
                        onMouseEnter={(_, index) => setActiveStageSlice(index)}
                        onMouseLeave={() => setActiveStageSlice(-1)}
                        onClick={(entry) => toggleDrill("stage", entry?.name)}
                        label={activeStageSlice === -1 && !drill.stage ? ({ percentage }) => `${percentage}` : false}
                      >
                        {data.stageBreakdown.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color || STAGE_COLORS[index % STAGE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(val, name, item) => [`${val} (${item.payload.percentage})`, name]}
                        {...darkTooltip}
                      />
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        wrapperStyle={chartCursor}
                        onClick={(entry) => toggleDrill("stage", entry?.value)}
                        formatter={(value) => (
                          <span
                            style={{
                              color: drill.stage === value ? "#0F172A" : "#475569",
                              fontWeight: drill.stage === value ? 800 : 500
                            }}
                          >
                            {value}
                          </span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>

            {/* Right: Number of Leads in Each Stage (Horizontal Bar Chart) */}
            <Grid item xs={12} md={6}>
              <ChartCard title="Number of Leads in Each Stage" hint="Click a bar to filter the table by stage">
                {loading ? (
                  <Skeleton variant="rectangular" height="100%" />
                ) : data.stageBreakdown.length === 0 ? (
                  <EmptyChart message="No stage counts available" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      layout="vertical"
                      data={data.stageBreakdown}
                      margin={{ top: 10, right: 35, left: 70, bottom: 5 }}
                      style={chartCursor}
                      onClick={(state) => toggleDrill("stage", state?.activeLabel)}
                    >
                      <defs>
                        <linearGradient id="stageBarGrad" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#2563EB" stopOpacity={0.9} />
                          <stop offset="100%" stopColor="#3B82F6" stopOpacity={1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#334155" }} />
                      <RechartsTooltip cursor={{ fill: "rgba(37,99,235,0.08)" }} {...darkTooltip} />
                      <Bar dataKey="count" fill="url(#stageBarGrad)" radius={[0, 6, 6, 0]}>
                        <LabelList dataKey="count" position="right" style={{ fill: "#1E293B", fontSize: 12, fontWeight: 700 }} />
                        {data.stageBreakdown.map((entry, index) => (
                          <Cell
                            key={`bar-cell-${index}`}
                            fill={entry.color || "url(#stageBarGrad)"}
                            fillOpacity={stageOpacity(entry.name)}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>
          </Grid>

          {/* 3. DAILY LEADS RECEIVED TREND (FULL WIDTH - MATCHING IMAGE 3) */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              bgcolor: "#FFFFFF",
              boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)"
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
              <Typography variant="h6" sx={{ fontSize: "1.05rem", color: "#0F172A", fontWeight: 800, textAlign: "center" }}>
                {trendMonthYearTitle}
              </Typography>
              <Tooltip title="Click a day to filter the table to that date" arrow>
                <Box sx={{ display: "flex", color: "#94A3B8" }}>
                  <MousePointerClick size={15} />
                </Box>
              </Tooltip>
            </Stack>
            <Box sx={{ width: "100%", height: 320 }}>
              {loading ? (
                <Skeleton variant="rectangular" height="100%" />
              ) : data.dailyTrend.length === 0 ? (
                <EmptyChart message="No daily lead data found" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.dailyTrend}
                    margin={{ top: 25, right: 20, left: 10, bottom: 45 }}
                    style={chartCursor}
                    onClick={(state) => toggleDrill("date", state?.activeLabel)}
                  >
                    <defs>
                      <linearGradient id="dailyBarGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4F46E5" stopOpacity={1} />
                        <stop offset="100%" stopColor="#06B6D4" stopOpacity={0.85} />
                      </linearGradient>
                      <linearGradient id="dailyBarGradActive" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#059669" stopOpacity={1} />
                        <stop offset="100%" stopColor="#047857" stopOpacity={0.9} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: "#475569", fontWeight: 500 }}
                      tickFormatter={(val) => (val ? String(val).replace("-", " ") : "")}
                      interval={getTickInterval(data.dailyTrend?.length)}
                      angle={-35}
                      textAnchor="end"
                      height={45}
                    />
                    <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
                    <RechartsTooltip cursor={{ fill: "rgba(99,102,241,0.08)" }} {...darkTooltip} />
                    <Bar dataKey="count" fill="url(#dailyBarGrad)" radius={[6, 6, 0, 0]}>
                      <LabelList dataKey="count" position="top" style={{ fill: "#1E293B", fontSize: 11, fontWeight: 700 }} />
                      {data.dailyTrend.map((entry) => (
                        <Cell
                          key={`day-${entry.date}`}
                          fill={drill.date === entry.date ? "url(#dailyBarGradActive)" : "url(#dailyBarGrad)"}
                          fillOpacity={dateOpacity(entry.date)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>

          {/* 4. ASSIGNEE BREAKDOWN SECTION (2 SIDE-BY-SIDE CHARTS - MATCHING IMAGE 4) */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Left: Leads Received by Each Assignee (Vertical Bar Chart) */}
            <Grid item xs={12} md={6}>
              <ChartCard title="Leads Received by Each Assignee" hint="Click a bar to filter the table by assignee">
                {loading ? (
                  <Skeleton variant="rectangular" height="100%" />
                ) : data.assigneeBreakdown.length === 0 ? (
                  <EmptyChart message="No assignee data available" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={data.assigneeBreakdown}
                      margin={{ top: 25, right: 20, left: 10, bottom: 20 }}
                      style={chartCursor}
                      onClick={(state) => toggleDrill("assignee", state?.activeLabel)}
                    >
                      <defs>
                        <linearGradient id="assBarGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1E3A8A" stopOpacity={1} />
                          <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.85} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#334155", fontWeight: 600 }} />
                      <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
                      <RechartsTooltip cursor={{ fill: "rgba(30,58,138,0.08)" }} {...darkTooltip} />
                      <Bar dataKey="count" fill="url(#assBarGrad)" radius={[6, 6, 0, 0]}>
                        <LabelList dataKey="count" position="top" style={{ fill: "#1E293B", fontSize: 12, fontWeight: 700 }} />
                        {data.assigneeBreakdown.map((entry, index) => (
                          <Cell
                            key={`ass-cell-${index}`}
                            fill={entry.color || "url(#assBarGrad)"}
                            fillOpacity={assigneeOpacity(entry.name)}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>

            {/* Right: Assignee Share of Leads (Pie Chart) */}
            <Grid item xs={12} md={6}>
              <ChartCard title="Assignee Share of Leads" hint="Click a slice or legend entry to filter the table by assignee">
                {loading ? (
                  <Skeleton variant="rectangular" height="100%" />
                ) : data.assigneeBreakdown.length === 0 ? (
                  <EmptyChart message="No assignee share available" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart style={chartCursor}>
                      <Pie
                        data={data.assigneeBreakdown}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={95}
                        innerRadius={45}
                        paddingAngle={2}
                        isAnimationActive={false}
                        shape={makePieSectorRenderer({
                          hoveredIndex: activeAssigneeSlice,
                          selectedName: drill.assignee,
                          colorAt: (i) => data.assigneeBreakdown[i]?.color || ASSIGNEE_COLORS[i % ASSIGNEE_COLORS.length]
                        })}
                        onMouseEnter={(_, index) => setActiveAssigneeSlice(index)}
                        onMouseLeave={() => setActiveAssigneeSlice(-1)}
                        onClick={(entry) => toggleDrill("assignee", entry?.name)}
                        label={activeAssigneeSlice === -1 && !drill.assignee ? ({ percentage }) => `${percentage}` : false}
                      >
                        {data.assigneeBreakdown.map((entry, index) => (
                          <Cell
                            key={`pie-ass-${index}`}
                            fill={entry.color || ASSIGNEE_COLORS[index % ASSIGNEE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(val, name, item) => [`${val} (${item.payload.percentage})`, name]}
                        {...darkTooltip}
                      />
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        wrapperStyle={chartCursor}
                        onClick={(entry) => toggleDrill("assignee", entry?.value)}
                        formatter={(value) => (
                          <span
                            style={{
                              color: drill.assignee === value ? "#0F172A" : "#475569",
                              fontWeight: drill.assignee === value ? 800 : 500
                            }}
                          >
                            {value}
                          </span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>
          </Grid>

          {/* 5. STACKED & CUMULATIVE SECTION (2 SIDE-BY-SIDE CHARTS - MATCHING IMAGE 5) */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Left: Daily Leads by Assignee (stacked) */}
            <Grid item xs={12} md={6}>
              <ChartCard
                title="Daily Leads by Assignee (stacked)"
                hint="Click a legend entry to show/hide an assignee, or a column to filter by day"
              >
                {loading ? (
                  <Skeleton variant="rectangular" height="100%" />
                ) : data.dailyAssigneeStacked.length === 0 ? (
                  <EmptyChart message="No daily assignee stack available" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={data.dailyAssigneeStacked}
                      margin={{ top: 20, right: 20, left: 10, bottom: 45 }}
                      style={chartCursor}
                      onClick={(state) => toggleDrill("date", state?.activeLabel)}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: "#475569", fontWeight: 500 }}
                        tickFormatter={(val) => (val ? String(val).replace("-", " ") : "")}
                        interval={getTickInterval(data.dailyAssigneeStacked?.length)}
                        angle={-35}
                        textAnchor="end"
                        height={45}
                      />
                      <YAxis />
                      <RechartsTooltip cursor={{ fill: "rgba(15,23,42,0.05)" }} {...darkTooltip} />
                      <Legend
                        verticalAlign="bottom"
                        wrapperStyle={chartCursor}
                        onClick={(entry) => toggleSeries(entry?.dataKey || entry?.value)}
                        formatter={(value) => (
                          <span
                            style={{
                              color: hiddenSeries.includes(value) ? "#CBD5E1" : "#475569",
                              textDecoration: hiddenSeries.includes(value) ? "line-through" : "none"
                            }}
                          >
                            {value}
                          </span>
                        )}
                      />
                      {data.assigneesList.map((assigneeName, index) => (
                        <Bar
                          key={assigneeName}
                          dataKey={assigneeName}
                          stackId="a"
                          hide={hiddenSeries.includes(assigneeName)}
                          fill={ASSIGNEE_COLORS[index % ASSIGNEE_COLORS.length]}
                          fillOpacity={assigneeOpacity(assigneeName)}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>

            {/* Right: Cumulative Leads — Month to Date */}
            <Grid item xs={12} md={6}>
              <ChartCard title="Cumulative Leads — Month to Date" hint="Click a point to filter the table to that day">
                {loading ? (
                  <Skeleton variant="rectangular" height="100%" />
                ) : data.cumulativeTrend.length === 0 ? (
                  <EmptyChart message="No cumulative data available" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart
                      data={data.cumulativeTrend}
                      margin={{ top: 20, right: 20, left: 10, bottom: 45 }}
                      style={chartCursor}
                      onClick={(state) => toggleDrill("date", state?.activeLabel)}
                    >
                      <defs>
                        <linearGradient id="cumAreaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: "#475569", fontWeight: 500 }}
                        tickFormatter={(val) => (val ? String(val).replace("-", " ") : "")}
                        interval={getTickInterval(data.cumulativeTrend?.length)}
                        angle={-35}
                        textAnchor="end"
                        height={45}
                      />
                      <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
                      <RechartsTooltip {...darkTooltip} />
                      <Area
                        type="monotone"
                        dataKey="cumulative"
                        stroke="#8B5CF6"
                        strokeWidth={3}
                        fill="url(#cumAreaGrad)"
                        dot={(props) => {
                          const isSelected = Boolean(drill.date) && drill.date === props?.payload?.date;
                          return (
                            <circle
                              key={`dot-${props?.payload?.date}`}
                              cx={props.cx}
                              cy={props.cy}
                              r={isSelected ? 6 : 2}
                              fill="#8B5CF6"
                              stroke="#FFF"
                              strokeWidth={2}
                            />
                          );
                        }}
                        activeDot={{ r: 6, fill: "#7C3AED", stroke: "#FFF", strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>
          </Grid>

          {/* 6. DETAILED LEADS DATA TABLE SECTION */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              bgcolor: "#FFFFFF",
              boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)"
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={2} mb={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontSize: "1.05rem", color: "#0F172A", fontWeight: 800 }}>
                  Lead Details Breakdown ({filteredDetails.length})
                </Typography>
                <Tooltip title="Sort by any column header; click a row for the full lead record" arrow>
                  <Box sx={{ display: "flex", color: "#94A3B8" }}>
                    <MousePointerClick size={15} />
                  </Box>
                </Tooltip>
              </Stack>
              <TextField
                size="small"
                placeholder="Search lead, company, phone, stage..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} color="#64748B" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")} aria-label="Clear search">
                        <X size={14} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
                sx={{ width: { xs: "100%", sm: 320 } }}
              />
            </Stack>

            <TableContainer sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {TABLE_COLUMNS.map((col) => (
                      <TableCell
                        key={col.id}
                        sortDirection={sort.key === col.id ? sort.dir : false}
                        sx={{ fontWeight: 700, color: "#475569", bgcolor: "#F8FAFC", whiteSpace: "nowrap" }}
                      >
                        <TableSortLabel
                          active={sort.key === col.id}
                          direction={sort.key === col.id ? sort.dir : "asc"}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, idx) => (
                      <TableRow key={idx}>
                        {Array.from({ length: TABLE_COLUMNS.length }).map((__, cIdx) => (
                          <TableCell key={cIdx}><Skeleton width="80%" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={TABLE_COLUMNS.length} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">No lead records found</Typography>
                        {activeDrillCount > 0 && (
                          <Button size="small" onClick={clearAllDrills} sx={{ mt: 1, fontWeight: 700 }}>
                            Clear drill-down filters
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((lead) => (
                      <TableRow
                        key={lead.id}
                        hover
                        onClick={() => setSelectedLead(lead)}
                        selected={selectedLead?.id === lead.id}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: "#1E293B" }}>
                          {lead.name}
                        </TableCell>
                        <TableCell sx={{ color: "#64748B" }}>
                          {lead.companyName || "—"}
                        </TableCell>
                        <TableCell>
                          {lead.phone ? (
                            <Link
                              href={`tel:${lead.phone}`}
                              underline="hover"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ display: "block", fontSize: "0.8rem", color: "#334155", fontWeight: 600 }}
                            >
                              {lead.phone}
                            </Link>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#94A3B8" }}>—</Typography>
                          )}
                          {lead.email && (
                            <Link
                              href={`mailto:${lead.email}`}
                              underline="hover"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ fontSize: "0.72rem", color: "#94A3B8" }}
                            >
                              {lead.email}
                            </Link>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lead.stage}
                            size="small"
                            clickable
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDrill("stage", lead.stage);
                            }}
                            sx={{
                              bgcolor: `${lead.stageColor || "#3B82F6"}18`,
                              color: lead.stageColor || "#1E3A8A",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              borderRadius: 1.5,
                              border: `1px solid ${lead.stageColor || "#3B82F6"}${drill.stage === lead.stage ? "FF" : "40"}`,
                              "&:hover": { bgcolor: `${lead.stageColor || "#3B82F6"}30` }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDrill("assignee", lead.accountManager);
                            }}
                            sx={{
                              cursor: "pointer",
                              borderRadius: 1,
                              px: 0.5,
                              py: 0.25,
                              bgcolor: drill.assignee === lead.accountManager ? "#E0F2FE" : "transparent",
                              "&:hover": { bgcolor: "#F1F5F9" }
                            }}
                          >
                            <Avatar sx={{ width: 22, height: 22, fontSize: "0.65rem", bgcolor: "#2563EB" }}>
                              {(lead.accountManager || "U").charAt(0)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#334155", whiteSpace: "nowrap" }}>
                              {lead.accountManager}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: "#64748B", fontSize: "0.8rem" }}>
                          {lead.source}
                        </TableCell>
                        <TableCell sx={{ color: "#64748B", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                          {new Date(lead.createdAt).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric"
                          })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredDetails.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>

          {/* 7. LEAD DETAIL SIDE PANEL */}
          <Drawer
            anchor="right"
            open={Boolean(selectedLead)}
            onClose={() => setSelectedLead(null)}
            PaperProps={{ sx: { width: { xs: "100%", sm: 400 } } }}
          >
            {selectedLead && (
              <Box>
                <Box sx={{ bgcolor: "#1E3A8A", color: "#FFF", p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: "1.1rem" }}>
                        {selectedLead.name}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {selectedLead.companyName || "No company"}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setSelectedLead(null)} sx={{ color: "#FFF" }} aria-label="Close details">
                      <X size={18} />
                    </IconButton>
                  </Stack>
                  <Chip
                    label={selectedLead.stage}
                    size="small"
                    sx={{
                      mt: 1.5,
                      bgcolor: "rgba(255,255,255,0.18)",
                      color: "#FFF",
                      fontWeight: 700,
                      fontSize: "0.72rem"
                    }}
                  />
                </Box>

                <Box sx={{ p: 2.5 }}>
                  <DetailRow
                    icon={<Phone size={16} />}
                    label="Phone"
                    value={
                      selectedLead.phone ? (
                        <Link href={`tel:${selectedLead.phone}`} underline="hover">{selectedLead.phone}</Link>
                      ) : null
                    }
                  />
                  <Divider />
                  <DetailRow
                    icon={<Mail size={16} />}
                    label="Email"
                    value={
                      selectedLead.email ? (
                        <Link href={`mailto:${selectedLead.email}`} underline="hover">{selectedLead.email}</Link>
                      ) : null
                    }
                  />
                  <Divider />
                  <DetailRow icon={<Building size={16} />} label="Company" value={selectedLead.companyName} />
                  <Divider />
                  <DetailRow icon={<User size={16} />} label="Assignee" value={selectedLead.accountManager} />
                  <Divider />
                  <DetailRow icon={<Layers size={16} />} label="Source" value={selectedLead.source} />
                  <Divider />
                  <DetailRow
                    icon={<CalendarIcon size={16} />}
                    label="Created"
                    value={new Date(selectedLead.createdAt).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  />

                  <Divider />
                  <DetailRow
                    icon={<FileText size={16} />}
                    label="Notes / Remarks"
                    value={
                      <Box
                        sx={{
                          mt: 0.8,
                          p: 1.5,
                          bgcolor: "#F8FAFC",
                          borderRadius: 2,
                          border: "1px solid #E2E8F0",
                          color: (selectedLead.notes || selectedLead.remarks) ? "#334155" : "#94A3B8",
                          fontSize: "0.82rem",
                          fontWeight: 500,
                          fontStyle: (selectedLead.notes || selectedLead.remarks) ? "normal" : "italic",
                          whiteSpace: "pre-line",
                          maxHeight: 180,
                          overflowY: "auto"
                        }}
                      >
                        {selectedLead.notes || selectedLead.remarks || "No notes available for this lead."}
                      </Box>
                    }
                  />
                </Box>
              </Box>
            )}
          </Drawer>

        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default UnreachedLeadsReport;
